import React from 'react';
import { Car, DollarSign, Gauge, Settings, CheckCircle, Star, ArrowRight, Fuel, Wrench, Circle, FileCheck, MapPin, TrendingDown } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-sm fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Car className="h-6 w-6 text-[#004747]" />
            <span className="text-xl font-semibold text-[#004747]">AutoSecurely</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Car className="h-12 w-12 text-[#004747]" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-[#004747] mb-6">
            The Hidden Costs of Driving in America
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-4">
            And Simple Ways to Start Saving Every Month
          </p>
          <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
            Most drivers spend thousands each year without realizing it.<br />
            Find out where your money goes — and how to keep more of it in your pocket.
          </p>
        </div>
      </section>

      {/* Where Your Money Goes */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <DollarSign className="h-10 w-10 text-[#F44B31]" />
            <h2 className="text-4xl font-bold text-[#004747]">Where Does Your Money Go?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mt-12">
            <div>
              <p className="text-lg text-gray-600 mb-8">
                Every day, your vehicle quietly drains your wallet through dozens of small expenses. 
                Here's how everyday car costs accumulate throughout the year:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Fuel className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Fuel: $1,800–$3,000</p>
                    <p className="text-sm text-gray-500">Annual gas expenses</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Wrench className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Maintenance & Repairs: $900–$1,200</p>
                    <p className="text-sm text-gray-500">Regular servicing and fixes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Settings className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Tires & Oil: $400–$700</p>
                    <p className="text-sm text-gray-500">Essential replacements</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileCheck className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Registration & Taxes: $300–$600</p>
                    <p className="text-sm text-gray-500">Annual fees and taxes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Parking & Tolls: $200–$500</p>
                    <p className="text-sm text-gray-500">Daily parking and toll fees</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingDown className="h-6 w-6 text-[#F44B31] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#004747]">Depreciation: up to $3,000</p>
                    <p className="text-sm text-gray-500">Vehicle value loss</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center">
                <p className="text-6xl font-bold text-[#F44B31] mb-4">$7-8K</p>
                <p className="text-xl text-gray-700 font-medium">Average Annual Cost</p>
                <p className="text-gray-500 mt-2">Just to stay mobile</p>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  That's an average of <span className="font-semibold text-[#004747]">$7,000–$8,000 per year</span> — just to stay mobile.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Ways to Save */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Settings className="h-10 w-10 text-[#F44B31]" />
            <h2 className="text-4xl font-bold text-[#004747]">Smart Ways to Save</h2>
          </div>
          <p className="text-center text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Even small changes can make a big difference.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Gauge, title: 'Track your mileage', desc: 'Monitor your driving habits' },
              { icon: Car, title: 'Drive smoothly', desc: 'Avoid aggressive acceleration' },
              { icon: Circle, title: 'Keep tires inflated', desc: 'Check pressure monthly' },
              { icon: MapPin, title: 'Plan your routes', desc: 'Optimize your trips' },
              { icon: Fuel, title: 'Avoid idling', desc: 'Turn off when parked' },
              { icon: Wrench, title: 'Regular checkups', desc: 'Prevent costly repairs' },
            ].map((tip, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-6">
                  <tip.icon className="h-12 w-12 text-[#F44B31] mb-4" />
                  <h3 className="text-lg font-semibold text-[#004747] mb-2">{tip.title}</h3>
                  <p className="text-gray-600 text-sm">{tip.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Most Drivers Overspend */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <TrendingDown className="h-10 w-10 text-[#F44B31]" />
            <h2 className="text-4xl font-bold text-[#004747]">Why Most Drivers Overspend</h2>
          </div>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Small things like skipping maintenance or driving aggressively slowly drain your wallet.
            Learning how to optimize your driving habits can save hundreds every year — without sacrificing comfort.
          </p>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="bg-red-50 rounded-xl p-8">
              <p className="text-lg font-semibold text-red-800 mb-4">Average Driver</p>
              <div className="space-y-3 text-left">
                <p className="text-gray-700">• Aggressive acceleration</p>
                <p className="text-gray-700">• Skips maintenance</p>
                <p className="text-gray-700">• Unplanned routes</p>
                <p className="text-gray-700">• Ignores tire pressure</p>
              </div>
              <p className="text-3xl font-bold text-red-600 mt-6">$8,000/yr</p>
            </div>
            <div className="bg-green-50 rounded-xl p-8">
              <p className="text-lg font-semibold text-green-800 mb-4">Smart Driver</p>
              <div className="space-y-3 text-left">
                <p className="text-gray-700">• Smooth acceleration</p>
                <p className="text-gray-700">• Regular maintenance</p>
                <p className="text-gray-700">• Optimized routes</p>
                <p className="text-gray-700">• Monitors tire pressure</p>
              </div>
              <p className="text-3xl font-bold text-green-600 mt-6">$6,500/yr</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-[#004747] text-center mb-12">Quick Tips for Smarter Driving</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Combine errands into one trip',
              'Keep your trunk light',
              'Clean your air filter',
              'Check tire pressure monthly',
              'Avoid rush hours when possible',
              'Use cruise control on highways',
              'Remove roof racks when not in use',
              'Service your vehicle on schedule',
            ].map((tip, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Drivers Results */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Star className="h-10 w-10 text-[#F44B31]" />
            <h2 className="text-4xl font-bold text-[#004747]">Real Drivers. Real Results.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-lg text-gray-700 mb-4 italic">
                  "Following a few small tips cut my monthly car costs by $60."
                </p>
                <p className="text-sm text-gray-500">— Angela R., Arizona</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-lg text-gray-700 mb-4 italic">
                  "Didn't realize how much my driving habits affected my gas use."
                </p>
                <p className="text-sm text-gray-500">— Chris M., Ohio</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-sm text-gray-400 mt-8">Results vary depending on car model and habits.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <CheckCircle className="h-12 w-12 text-[#F44B31]" />
          </div>
          <h2 className="text-5xl font-bold text-[#004747] mb-6">Start Your Savings Journey Today</h2>
          <p className="text-xl text-gray-600 mb-4 max-w-2xl mx-auto">
            Discover how small tweaks can make a big impact on your monthly costs.
          </p>
          <p className="text-lg text-gray-500 mb-8">
            Learn where your money goes — and how to keep more of it in your pocket.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#004747] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Car className="h-6 w-6" />
            <span className="text-xl font-semibold">AutoSecurely</span>
          </div>
          <p className="text-gray-300 text-sm max-w-md">
            Helping American drivers save money through smarter driving habits.
          </p>
          <div className="border-t border-gray-600 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 AutoSecurely. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;